export type ScoringMethod = 'stroke_play_net' | 'stableford_net';

interface HoleScore {
  hole: number;
  strokes: number;
}

interface HoleInfo {
  par: number;
  handicapIndex: number;
}

function strokesReceived(handicap: number, holeHcpIndex: number): number {
  if (handicap >= 18) {
    return 1 + (holeHcpIndex <= handicap - 18 ? 1 : 0);
  }
  return holeHcpIndex <= handicap ? 1 : 0;
}

export function calcStrokePlayNet(
  holes: HoleScore[],
  handicap: number,
  holeParMap: Record<number, number>,
  holeHcpMap: Record<number, number>,
  totalHolesInRound?: number,
): number {
  const totalStrokes = holes.reduce((sum, h) => sum + h.strokes, 0);
  const totalPar = holes.reduce((sum, h) => sum + (holeParMap[h.hole] ?? 4), 0);
  const registeredHoles = totalHolesInRound ?? holes.length;
  if (registeredHoles >= 18) {
    return totalStrokes - totalPar - handicap;
  }
  const proportionalHandicap = Math.round(handicap * registeredHoles / 18);
  return totalStrokes - totalPar - proportionalHandicap;
}

export function calcStablefordNet(
  holes: HoleScore[],
  handicap: number,
  holeParMap: Record<number, number>,
  holeHcpMap: Record<number, number>,
): number {
  let totalPoints = 0;
  for (const h of holes) {
    const par = holeParMap[h.hole] ?? 4;
    const hcpIdx = holeHcpMap[h.hole] ?? h.hole;
    const given = strokesReceived(Math.floor(handicap), hcpIdx);
    const netStrokes = h.strokes - given;
    const diff = netStrokes - par;
    // Stableford: 2 for par, +1 for each stroke under, -1 for each stroke over, min 0
    const points = Math.max(0, 2 - diff);
    totalPoints += points;
  }
  return totalPoints;
}

export function calcRoundScore(
  holes: HoleScore[],
  handicap: number,
  holeParMap: Record<number, number>,
  holeHcpMap: Record<number, number>,
  scoringMethod: ScoringMethod,
  totalHolesInRound?: number,
): number {
  if (scoringMethod === 'stableford_net') {
    return calcStablefordNet(holes, handicap, holeParMap, holeHcpMap);
  }
  return calcStrokePlayNet(holes, handicap, holeParMap, holeHcpMap, totalHolesInRound);
}

export function computeLeaderboard(
  entries: { id: string; handicap: number; [key: string]: unknown }[],
  holeScoreMap: Record<string, { round: number; hole: number; strokes: number; score_status?: string }[]>,
  holeParMap: Record<number, number>,
  holeHcpMap: Record<number, number>,
  scoringMethod: ScoringMethod,
  enabledRounds?: Set<number>,
): { id: string; total_score: number; round_scores: (number | null)[]; rounds_played: number; thru: number }[] {
  return entries.map(entry => {
    const playerHoles = holeScoreMap[entry.id];
    if (!playerHoles || playerHoles.length === 0) {
      return { id: entry.id, total_score: 0, round_scores: [], rounds_played: 0, thru: 0 };
    }

    const handicap = Math.floor(entry.handicap ?? 0);
    const roundHoleMap: Record<number, HoleScore[]> = {};
    const roundHoleCountMap: Record<number, Set<number>> = {};
    const roundSubmitted: Record<number, boolean> = {};
    const roundHoleDedup: Record<number, Map<number, HoleScore>> = {};
    for (const h of playerHoles) {
      if (h.hole <= 0) continue;
      if (!roundHoleCountMap[h.round]) roundHoleCountMap[h.round] = new Set();
      roundHoleCountMap[h.round].add(h.hole);
      if (h.score_status === 'submitted' || h.score_status === 'verified') {
        roundSubmitted[h.round] = true;
      }
      if (h.strokes <= 0) continue;
      if (!roundHoleDedup[h.round]) roundHoleDedup[h.round] = new Map();
      roundHoleDedup[h.round].set(h.hole, { hole: h.hole, strokes: h.strokes });
    }
    for (const [r, holeMap] of Object.entries(roundHoleDedup)) {
      roundHoleMap[Number(r)] = Array.from(holeMap.values());
    }

    const allRounds = new Set([...Object.keys(roundHoleMap).map(Number), ...Object.keys(roundHoleCountMap).map(Number)]);
    const maxRound = allRounds.size > 0 ? Math.max(...allRounds) : 0;
    const roundScores: (number | null)[] = [];
    let total = 0;
    let roundsPlayed = 0;
    let latestEnabledRound = 0;

    for (let r = 1; r <= maxRound; r++) {
      const holes = roundHoleMap[r];
      if (!holes || holes.length === 0) {
        roundScores.push(null);
        if (roundHoleCountMap[r] && roundHoleCountMap[r].size > 0) {
          const isEnabled = !enabledRounds || enabledRounds.has(r);
          if (isEnabled) latestEnabledRound = r;
        }
        continue;
      }
      const totalHolesRegistered = roundHoleCountMap[r] ? roundHoleCountMap[r].size : holes.length;
      const score = calcRoundScore(holes, handicap, holeParMap, holeHcpMap, scoringMethod, totalHolesRegistered);
      roundScores.push(score);
      const isEnabled = !enabledRounds || enabledRounds.has(r);
      if (isEnabled) {
        total += score;
        roundsPlayed++;
        latestEnabledRound = r;
      }
    }

    let thru: number;
    if (latestEnabledRound > 0 && roundSubmitted[latestEnabledRound]) {
      thru = 18;
    } else if (latestEnabledRound > 0 && roundHoleCountMap[latestEnabledRound]) {
      thru = roundHoleCountMap[latestEnabledRound].size;
    } else {
      thru = 0;
    }

    return { id: entry.id, total_score: total, round_scores: roundScores, rounds_played: roundsPlayed, thru };
  });
}

export function sortLeaderboard<T extends { total_score: number; handicap: number; player_name: string; overall_status?: string }>(
  entries: T[],
  scoringMethod: ScoringMethod,
): T[] {
  return [...entries].sort((a, b) => {
    const aStatus = a.overall_status || 'NO_SCORE';
    const bStatus = b.overall_status || 'NO_SCORE';
    if (aStatus === 'PLAYED' && bStatus !== 'PLAYED') return -1;
    if (aStatus !== 'PLAYED' && bStatus === 'PLAYED') return 1;
    if (aStatus === 'PLAYED' && bStatus === 'PLAYED') {
      if (a.total_score !== b.total_score) {
        // Stableford: higher is better; Stroke play: lower is better
        return scoringMethod === 'stableford_net'
          ? b.total_score - a.total_score
          : a.total_score - b.total_score;
      }
      return a.handicap - b.handicap;
    }
    return a.player_name.localeCompare(b.player_name);
  });
}

export function formatScore(score: number, scoringMethod: ScoringMethod): string {
  if (scoringMethod === 'stableford_net') {
    return `${score} pts`;
  }
  if (score === 0) return 'E';
  if (score >= 80) return '--';
  return score > 0 ? `+${score}` : score.toString();
}

export function formatRoundScore(score: number | null, scoringMethod: ScoringMethod): string {
  if (score === null || score === undefined) return '--';
  if (scoringMethod === 'stableford_net') {
    return `${score}`;
  }
  if (score === 0) return 'E';
  return score > 0 ? `+${score}` : score.toString();
}

export function scoreColor(score: number | null, scoringMethod: ScoringMethod): string {
  if (score === null) return 'text-gray-400';
  if (scoringMethod === 'stableford_net') {
    if (score >= 36) return 'text-emerald-600';
    if (score >= 30) return 'text-gray-700';
    return 'text-red-500';
  }
  if (score < 0) return 'text-emerald-600';
  if (score === 0) return 'text-gray-700';
  return 'text-red-500';
}

export function scoreColorHex(score: number | null, scoringMethod: ScoringMethod): string {
  if (score === null) return '#9ca3af';
  if (scoringMethod === 'stableford_net') {
    if (score >= 36) return '#059669';
    if (score >= 30) return '#374151';
    return '#dc2626';
  }
  if (score < 0) return '#16a34a';
  if (score === 0) return '#374151';
  return '#dc2626';
}
